import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Toaster, toast } from 'sonner';
import axios from 'axios';

import { IdeaList } from '../components/IdeaList';
import { IdeaEditor } from '../components/IdeaEditor';
import { SettingsDialog } from '../components/SettingsDialog';
import { ExportDialog } from '../components/ExportDialog';
import { OnboardingDialog } from '../components/OnboardingDialog';

import {
    initDB,
    createIdea,
    getAllIdeas,
    updateIdea,
    deleteIdea,
    duplicateIdea,
    importIdeas,
    isFirstTimeUser,
    markOnboardingSeen,
    getSetting,
    getApiKey,
} from '../lib/db';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AppPage = () => {
    const navigate = useNavigate();
    
    const [ideas, setIdeas] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showExport, setShowExport] = useState(false);

    // AI configuration
    const [aiMode, setAiMode] = useState('managed'); // 'managed' | 'byok'
    const [aiProvider, setAiProvider] = useState('openai');

    // Independent AI state per tool
    const [aiLoading, setAiLoading] = useState(false);
    const [aiResults, setAiResults] = useState({ refine: null, roadmap: null, critique: null });
    const [aiErrors, setAiErrors] = useState({ refine: null, roadmap: null, critique: null });

    const selectedIdea = ideas.find(idea => idea.id === selectedId);

    // Auto-collapse sidebar on smaller screens
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 1280 && window.innerWidth >= 1024) {
                setSidebarCollapsed(true);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    useEffect(() => {
        const init = async () => {
            try {
                await initDB();

                // Load AI settings
                const [storedMode, storedProvider] = await Promise.all([
                    getSetting('aiMode'),
                    getSetting('aiProvider'),
                ]);
                setAiMode(storedMode || 'managed');
                setAiProvider(storedProvider || 'openai');

                const firstTime = await isFirstTimeUser();
                if (firstTime) {
                    setShowOnboarding(true);
                }
                
                const loadedIdeas = await getAllIdeas();
                setIdeas(loadedIdeas);
                
            } catch (error) {
                console.error('Failed to initialize:', error);
                toast.error('Failed to load your ideas');
            } finally {
                setIsLoading(false);
            }
        };
        
        init();
    }, []);

    // Clear AI results when switching ideas
    useEffect(() => {
        setAiResults({ refine: null, roadmap: null, critique: null });
        setAiErrors({ refine: null, roadmap: null, critique: null });
    }, [selectedId]);
    
    const handleOnboardingClose = async () => {
        await markOnboardingSeen();
        setShowOnboarding(false);
    };
    
    const handleNewIdea = async () => {
        try {
            const newIdea = await createIdea({
                id: uuidv4(),
                title: '',
                content: '',
            });
            setIdeas(prev => [newIdea, ...prev]);
            setSelectedId(newIdea.id);
        } catch (error) {
            console.error('Failed to create idea:', error);
            toast.error('Failed to create idea');
        }
    };
    
    const handleSaveIdea = useCallback(async (id, updates) => {
        setIsSaving(true);
        try {
            const updated = await updateIdea(id, updates);
            setIdeas(prev => prev.map(idea => 
                idea.id === id ? updated : idea
            ));
            setLastSaved(new Date().toISOString());
        } catch (error) {
            console.error('Failed to save idea:', error);
            toast.error('Failed to save');
        } finally {
            setIsSaving(false);
        }
    }, []);
    
    const handleDeleteIdea = async (id) => {
        try {
            await deleteIdea(id);
            setIdeas(prev => prev.filter(idea => idea.id !== id));
            if (selectedId === id) {
                setSelectedId(null);
            }
            toast.success('Idea deleted');
        } catch (error) {
            console.error('Failed to delete idea:', error);
            toast.error('Failed to delete');
        }
    };
    
    const handleDuplicateIdea = async (id) => {
        try {
            const duplicated = await duplicateIdea(id);
            setIdeas(prev => [duplicated, ...prev]);
            setSelectedId(duplicated.id);
            toast.success('Idea duplicated');
        } catch (error) {
            console.error('Failed to duplicate idea:', error);
            toast.error('Failed to duplicate');
        }
    };
    
    const handleImportIdeas = async (importedIdeas) => {
        try {
            await importIdeas(importedIdeas);
            const allIdeas = await getAllIdeas();
            setIdeas(allIdeas);
        } catch (error) {
            console.error('Failed to import ideas:', error);
            throw error;
        }
    };
    
    // AI action with independent results per tool
    const handleAIAction = async (toolId, { title, content, customPrompt }) => {
        setAiLoading(true);
        // Clear only the specific tool's previous result/error
        setAiResults(prev => ({ ...prev, [toolId]: null }));
        setAiErrors(prev => ({ ...prev, [toolId]: null }));

        try {
            let provider = 'openai';
            let apiKeyToSend = null;

            if (aiMode === 'byok') {
                provider = aiProvider;
                const keyRecord = await getApiKey(aiProvider);
                if (!keyRecord?.encryptedKey) {
                    throw new Error('No API key saved for selected provider');
                }
                // Send encrypted key; backend treats it as opaque and uses it directly with provider
                apiKeyToSend = keyRecord.encryptedKey;
            }

            const response = await axios.post(`${API}/ai/${toolId}`, {
                title,
                content,
                provider,
                apiKey: apiKeyToSend,
                customPrompt: toolId === 'refine' ? customPrompt : undefined,
            });

            // Set result only for this specific tool
            setAiResults(prev => ({ ...prev, [toolId]: response.data.result }));
        } catch (error) {
            console.error('AI call failed:', error);
            const message = error.response?.data?.detail || error.message || 'AI request failed';
            setAiErrors(prev => ({ ...prev, [toolId]: message }));
        } finally {
            setAiLoading(false);
        }
    };
    
    if (isLoading) {
        return (
            <div className="h-screen flex items-center justify-center app-gradient">
                <div className="text-center animate-fade-in">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-card shadow-sm flex items-center justify-center">
                        <svg className="w-7 h-7 text-indigo-400" fill="none" viewBox="0 0 48 48">
                            <path d="M42.1739 20.1739L27.8261 5.82609C29.1366 7.13663 28.3989 10.1876 26.2002 13.7654C24.8538 15.9564 22.9595 18.3449 20.6522 20.6522C18.3449 22.9595 15.9564 24.8538 13.7654 26.2002C10.1876 28.3989 7.13663 29.1366 5.82609 27.8261L20.1739 42.1739C21.4845 43.4845 24.5355 42.7467 28.1133 40.548C30.3042 39.2016 32.6927 37.3073 35 35C37.3073 32.6927 39.2016 30.3042 40.548 28.1133C42.7467 24.5355 43.4845 21.4845 42.1739 20.1739Z" fill="currentColor"/>
                        </svg>
                    </div>
                    <h1 className="text-2xl font-light text-foreground mb-1">IdeaVault</h1>
                    <p className="text-muted-foreground">Loading your ideas...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="h-screen flex overflow-hidden" data-testid="app-container">
            <IdeaList
                ideas={ideas}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onNew={handleNewIdea}
                onDelete={handleDeleteIdea}
                onDuplicate={handleDuplicateIdea}
                onOpenSettings={() => setShowSettings(true)}
                onOpenExport={() => setShowExport(true)}
                collapsed={sidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
                onBackToLanding={() => navigate('/')}
                mobileOpen={mobileSidebarOpen}
                onMobileClose={() => setMobileSidebarOpen(false)}
            />
            
            <IdeaEditor
                idea={selectedIdea}
                onSave={handleSaveIdea}
                isSaving={isSaving}
                lastSaved={lastSaved}
                onAIAction={handleAIAction}
                aiLoading={aiLoading}
                aiResults={aiResults}
                aiErrors={aiErrors}
                onOpenMobileSidebar={() => setMobileSidebarOpen(true)}
            />
            
            <SettingsDialog
                open={showSettings}
                onClose={() => setShowSettings(false)}
            />
            
            <ExportDialog
                open={showExport}
                onClose={() => setShowExport(false)}
                ideas={ideas}
                onImport={handleImportIdeas}
            />
            
            <OnboardingDialog
                open={showOnboarding}
                onClose={handleOnboardingClose}
            />
            
            <Toaster 
                position="bottom-right"
                toastOptions={{
                    className: 'font-sans',
                    style: {
                        background: 'hsl(var(--card))',
                        color: 'hsl(var(--foreground))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '12px',
                    },
                }}
            />
        </div>
    );
};

export default AppPage;
